<template>
  <div class="table-wrapper">
    <div class="table-header">
      <div class="legend">
        <div class="legend-item">
          <span class="circle badge-danger"></span>
          <span>Ausente</span>
        </div>
        <div class="legend-item">
          <span class="circle badge-warning"></span>
          <span>Presente (entrada/saída)</span>
        </div>
        <div class="legend-item">
          <span class="circle badge-success"></span>
          <span>Presente</span>
        </div>
      </div>

      <div class="actions">
        <IconButton 
          @handle:click="exportPDF" 
          title="Exportar como PDF"
          color="primary"
          :disabled="loading"
        >
          <FileOutline :size="18" />
        </IconButton>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <LoadingSpinner :size="60" />
    </div>

    <div v-else class="table-responsive">
      <Table :headers="tableHeaders">
        <tr v-for="(aluno, index) in alunos" :key="index">
          <td>{{ aluno.nome }}</td>
          <td>{{ aluno.cpf }}</td>
          <td>{{ aluno.entidade }}</td>
          <td>
            <span 
              class="circle" 
              :class="{
                'badge-danger': aluno.presenca === 0,
                'badge-warning': aluno.presenca === 1,
                'badge-success': aluno.presenca === 2
              }"
              :title="getPresencaText(aluno.presenca)"
            ></span>
          </td>
          <td>{{ getStatusText(aluno) }}</td>
          <td>
            <div v-if="aluno.presenca === 1 || aluno.presenca === 2">
              <IconButton
                @handle:click="viewPresenca(aluno)"
                color="primary"
                title="Visualizar Foto"
                size="small"
              >
                <ImageOutline :size="18" />
              </IconButton>
            </div>
            <div v-else>
              AUSENTE
            </div>
          </td>
          <td>{{ aluno.dispositivo }}</td>
          <td v-if="isAdmin && idRole !== 3">
            <IconButton
              v-if="aluno.presenca !== 2"
              @handle:click="openModalPresenca(aluno)"
              color="primary"
              title="Adicionar Presença"
              size="small"
            >
              <PlusCircleOutline :size="18" />
            </IconButton>
            <IconButton
              v-else
              color="default"
              disabled
              size="small"
            >
              <PlusCircleOutline :size="18" />
            </IconButton>
          </td>
        </tr>
        
        <tr v-if="alunos.length === 0">
          <td :colspan="isAdmin ? 8 : 7" class="text-center">
            Nenhum registro encontrado.
          </td>
        </tr>
      </Table>
    </div>

    <!-- Modal de Presença -->
    <Modal ref="presencaModal" title="Presença Aluno">
      <div class="presenca-container">
        <Table :headers="['FOTO', 'TIPO', 'HORÁRIO']">
          <tr v-for="(presenca, index) in presencaData" :key="index">
            <td>
              <img class="presenca-img" :src="'data:image/jpg;base64, ' + presenca.foto" alt="Foto de presença">
            </td>
            <td>
              <span v-if="presenca.tipo == 0">Entrada</span>
              <span v-else-if="presenca.tipo == 1">Saída</span>
              <span v-else-if="presenca.tipo == 2">Intervalo</span>
              <span v-else>{{ presenca.tipo }}</span>
            </td>
            <td>{{ presenca.created_at }}</td>
          </tr>
        </Table>

        <div class="fotos-teoricas">
          <h5>FOTOS AULAS TEÓRICAS</h5>
          <div class="fotos-grid">
            <div v-for="(foto, index) in teoricasImages" :key="index" class="foto-item">
              <img :src="'data:image/jpg;base64, ' + foto" alt="Foto de aula">
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Modal de Adicionar Presença -->
    <Modal ref="addPresencaModal" title="Adicionar Presença">
      <div class="add-presenca-container">
        <h3>{{ selectedAluno.nome }}</h3>
        
        <div class="presenca-form">
          <div class="presenca-inputs">
            <div class="input-group">
              <label>Entrada</label>
              <Input legend="entrada" type="time" :modelValue="selectedAluno.entrada" @update:modelValue="updateEntrada" />
            </div>
            
            <div class="input-group">
              <label>Intervalo</label>
              <Input 
                type="time" 
                legend="intervalo"
                :modelValue="selectedAluno.intervalo" 
                @update:modelValue="updateIntervalo"
                :disabled="selectedAluno.entidade !== 'ALUNO'"
              />
            </div>
            
            <div class="input-group">
              <label>Saída</label>
              <Input type="time" legend="saida" :modelValue="selectedAluno.saida" @update:modelValue="updateSaida" />
            </div>
          </div>
          
          <Button 
            @handle:click="marcarPresenca" 
            text="Confirmar Presença" 
            color="primary"
            :disabled="isSubmitting"
          >
            <CheckCircleOutline :size="18" />
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import * as spinners from 'epic-spinners'
import urlApi from '../../../router/urlApi'
import utils from '../../../services/utils'
import Table from 'core/Table'
import Input from 'core/Input'
import Button from 'core/Button'
import IconButton from 'core/IconButton'
import LoadingSpinner from 'core/LoadingSpinner'
import Modal from 'core/Modal'
import FileOutline from 'icons/FileOutline'
import ImageOutline from 'icons/ImageOutline'
import PlusCircleOutline from 'icons/PlusCircleOutline'
import CheckCircleOutline from 'icons/CheckCircleOutline'
import { debounce } from 'lodash'

export default {
  name: 'TableAlunos',
  components: {
    ...spinners,
    Table,
    Input,
    Button,
    IconButton,
    LoadingSpinner,
    Modal,
    FileOutline,
    ImageOutline,
    PlusCircleOutline,
    CheckCircleOutline
  },
  props: {
    idTurma: {
      type: [String, Number],
      required: true
    },
    virtual: {
      type: Boolean,
      default: false
    },
    idRole: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      search: '',
      alunos: [],
      loading: false,
      isSubmitting: false,
      tableHeaders: ['Nome', 'CPF', 'Entidade', 'Presença', 'Status', 'Foto Presença', 'Dispositivo'],
      isAdmin: false,
      presencaData: [],
      teoricasImages: [],
      selectedAluno: {
        id: '',
        nome: '',
        entidade: '',
        entrada: '',
        intervalo: '',
        saida: ''
      },
      utils
    }
  },
  created() {
    const email = localStorage.email ? localStorage.email.split('@')[0] : '';
    this.isAdmin = email === 'admin';
    
    // Se o usuário for admin, adicionar coluna de ações
    if (this.isAdmin && this.idRole !== 3) {
      this.tableHeaders.push('Adicionar Presença');
    }
  },
  mounted() {
    this.fetchAlunos();
  },
  methods: {
    async fetchAlunos() {
      this.loading = true;
      try {
        const response = await this.$http.get(`${urlApi}/aulas/teoricas/presenca/${this.idTurma}?search=${this.search}`);
        this.alunos = response.data;
      } catch (error) {
        this.toast('Erro ao carregar dados de presença', 'error');
      } finally {
        this.loading = false;
      }
    },
    getPresencaText(presenca) {
      switch (presenca) {
        case 0: return 'Ausente';
        case 1: return 'Semi-presente';
        case 2: return 'Presente';
        default: return 'Desconhecido';
      }
    },
    getStatusText(aluno) {
      if (aluno.status) return aluno.status;
      if (!aluno.status && aluno.entidade === 'ALUNO') return 'ALUNO REPOSIÇÃO';
      return 'INSTRUTOR';
    },
    async viewPresenca(aluno) {
      try {
        const id_entidade = aluno.id_aluno !== undefined ? aluno.id_aluno : aluno.id_instrutor;
        const entidade = aluno.entidade;
        const virtual = this.virtual;
        
        const response = await this.$http.get(
          `${urlApi}/alunos/detalhes-presencas/${id_entidade}/${this.idTurma}/${entidade}/${virtual}`
        );
        
        this.presencaData = response.data.dados.presencas || [];
        this.teoricasImages = response.data.dados.fotos_teoricas.map(item => item.foto) || [];
        
        this.$refs.presencaModal.open();
      } catch (error) {
        this.toast('Erro ao carregar detalhes de presença', 'error');
      }
    },
    openModalPresenca(aluno) {
      this.selectedAluno = {
        id: aluno.id_aluno || aluno.id_instrutor,
        nome: aluno.nome,
        entidade: aluno.entidade,
        entrada: '',
        intervalo: '',
        saida: ''
      };
      
      this.$refs.addPresencaModal.open();
    },
    updateEntrada(value) {
      this.selectedAluno.entrada = value;
    },
    updateIntervalo(value) {
      this.selectedAluno.intervalo = value;
    },
    updateSaida(value) {
      this.selectedAluno.saida = value;
    },
    async marcarPresenca() {
      // Validação
      if (this.selectedAluno.entidade === 'INSTRUTOR') {
        if (!this.selectedAluno.entrada || !this.selectedAluno.saida) {
          return this.toast('Necessário informar todos os campos!', 'error');
        }
      } else {
        if (!this.selectedAluno.entrada || !this.selectedAluno.saida) {
          return this.toast('Necessário informar pelo menos os campos de entrada e saída', 'error');
        }
      }
      
      this.isSubmitting = true;
      
      try {
        const aula = {
          turma_id: this.idTurma,
          id: this.selectedAluno.id,
          nome: this.selectedAluno.nome,
          entidade: this.selectedAluno.entidade,
          entrada: this.selectedAluno.entrada,
          intervalo: this.selectedAluno.intervalo,
          saida: this.selectedAluno.saida
        };
        
        const response = await this.$http.post(`${urlApi}/turmas/adicionar/presenca`, { aula });
        
        this.toast(response.data.mensagem, response.data.mensagem.includes('sucesso') ? 'success' : 'error');
        
        if (response.data.mensagem === 'Presença efetuada com sucesso!') {
          this.$refs.addPresencaModal.close();
          await this.fetchAlunos();
        }
      } catch (error) {
        this.toast('Erro ao registrar presença', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },
    exportPDF() {
      try {
        this.utils.generatePDFTurmas(
          this.idTurma,
          '/turmas/presencas/arquivo',
          'pdf',
          'Presenças'
        );
        this.toast('Exportação iniciada com sucesso!', 'success');
      } catch (error) {
        this.toast('Erro ao exportar PDF', 'error');
      }
    },
    toast(text, icon) {
      this.$toasted.show(text, {
        icon: icon,
        position: 'top-right',
        duration: 5000,
        fullWidth: false,
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.table-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;

  .legend {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }
  
  .actions {
    display: flex;
    align-items: flex-end;
    gap: 1rem;
    
    .search-container {
      width: 300px;
    }
  }
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.circle {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  display: inline-block;

  &.badge-danger {
    background-color: #dc3545;
  }
  
  &.badge-warning {
    background-color: #ffc107;
  }
  
  &.badge-success {
    background-color: #28a745;
  }
}

.text-center {
  text-align: center;
}

.presenca-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .presenca-img {
    max-width: 100px;
    height: auto;
  }

  .fotos-teoricas {
    h5 {
      font-weight: 600;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid #eee;
    }

    .fotos-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      gap: 1rem;

      .foto-item img {
        max-width: 100%;
        height: auto;
        border-radius: 4px;
      }
    }
  }
}

.add-presenca-container {
  h3 {
    margin-bottom: 1.5rem;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .presenca-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .presenca-inputs {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;

      .input-group {
        flex: 1;
        min-width: 120px;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        label {
          font-weight: 500;
          font-size: 0.875rem;
          color: #6c757d;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    
    .actions {
      width: 100%;
      flex-direction: column;
      align-items: flex-start;
      
      .search-container {
        width: 100%;
      }
    }
  }

  .add-presenca-container .presenca-inputs {
    flex-direction: column;
  }
}
</style>
